import isNil from 'lodash/isNil';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { WizardNavItemProps } from '@valid-eval/shared-react-components/dist/components/Wizard/WizardNavIitem';
import { Document } from 'data/features/documentsTypes';
import { Event, EventUser } from 'data/features/eventsTypes';
import { Phase } from 'data/features/phaseTypes';

import { EditEventSteps, Step } from '../index';

/**
 * Parameters for progress calculation functions
 */
type ProgressCalculationParams = {
  event: Event;
  phases: Phase[];
  contents: Record<string, string>;
  navigators: EventUser[];
  organizers: EventUser[];
  documents: Document[];
  eventKey: Step['eventKey'];
};

/**
 * List of required fields for an event to be considered complete.
 */
const eventRequiredFields = ['name', 'logo_url', 'description', 'slug', 'aggregate_amount'];

/**
 * Calculates the progress of required fields in a given object.
 *
 * @param object - The object to check for required fields.
 * @param requiredFields - Array of required field names.
 * @returns {Object} An object with completed, total, and percentage fields.
 *
 * @example
 * const progress = objectProgress(event, ['name', 'slug']);
 * progress = { completed: 1, total: 2, percentage: 50 }
 */
const objectProgress = (object: any, requiredFields: string[]) => {
  const total = requiredFields.length;
  const requiredFieldsCount = requiredFields.filter((field) => {
    const value = object[field as keyof typeof object];
    return isNil(value) || value === '';
  });
  const completed = total - requiredFieldsCount.length;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
};

/**
 * Calculates the progress of required event fields.
 *
 * @param params - Progress calculation parameters
 * @returns {Object} An object with completed, total, and percentage fields.
 *
 * @example
 * const progress = eventProgress(params);
 * progress = { completed: 3, total: 5, percentage: 60 }
 */
const eventProgress = ({ event }: ProgressCalculationParams) => {
  return objectProgress(event, eventRequiredFields);
};

/**
 * List of required fields for a phase to be considered complete.
 */
const phaseRequiredFields = [
  'name',
  'teams_anticipated_next_round',
  'start_date',
  'end_date',
  'consensus_meeting_date',
  'feedback_available_date',
];

/**
 * Calculates the progress of required phase fields.
 *
 * @param phase - The phase object to check.
 * @returns {Object} An object with completed, total, and percentage fields.
 *
 * @example
 * const progress = phaseProgress(phase);
 * progress = { completed: 5, total: 6, percentage: 83 }
 */
export const phaseProgress = (phase: Phase) => {
  return objectProgress(phase, phaseRequiredFields);
};

/**
 * Calculates the overall progress for all phases in an event.
 *
 * @param params - Progress calculation parameters
 * @returns {Object} An object with completed, total, and percentage fields.
 *
 * @example
 * const progress = phasesProgress(params);
 * progress = { completed: 2, total: 3, percentage: 67 }
 */
const phasesProgress = ({ phases }: ProgressCalculationParams) => {
  const total = phases.length;
  const phasesProgress = phases.map((phase) => phaseProgress(phase));
  const completed = phasesProgress.filter((progress) => progress.percentage === 100).length;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
};

/**
 * Required content fields for team signup process
 */
const teamRequiredFields = [
  'contents_team_signup_instructions',
  'contents_team_signup_confirmation_notice',
];

/**
 * Calculates the completion progress for team-related content fields
 * @param params - Progress calculation parameters
 * @returns Progress percentage for team content completion
 */
const teamsProgress = ({ contents }: ProgressCalculationParams) => {
  return objectProgress(contents, teamRequiredFields);
};

/**
 * Required content fields for judge signup process
 */
const judgeRequiredFields = [
  'contents_judge_signup_instructions',
  'contents_judge_signup_confirmation_notice',
];

/**
 * Calculates the completion progress for judge-related content fields
 * @param params - Progress calculation parameters
 * @returns Progress percentage for judge content completion
 */
const judgesProgress = ({ contents }: ProgressCalculationParams) => {
  return objectProgress(contents, judgeRequiredFields);
};

/**
 * Calculates the completion progress for users (organizers or navigators)
 * @param params - Progress calculation parameters
 * @returns Progress object with completed, total, and percentage
 */
const userProgress = ({ navigators, organizers, eventKey }: ProgressCalculationParams) => {
  const total = 1;
  const completed =
    (eventKey === 'navigators' ? (navigators?.length ?? 0) : (organizers?.length ?? 0)) > 0 ? 1 : 0;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
};

/**
 * Calculates the completion progress for artifacts
 * @param params - Progress calculation parameters
 * @returns Progress object with completed, total, and percentage
 */
const artifactsProgress = ({ event }: ProgressCalculationParams) => {
  const total = 1;
  const completed = (event.artifacts?.length ?? 0) > 0 ? 1 : 0;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
};

/**
 * Calculates the completion progress for documents
 * @param params - Progress calculation parameters
 * @returns Progress object with completed, total, and percentage
 */
const documentsProgress = ({ documents }: ProgressCalculationParams) => {
  const total = 1;
  const completed = (documents?.length ?? 0) > 0 ? 1 : 0;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
};

/**
 * Calculates the completion progress for a specific section of an event
 * @param params - Progress calculation parameters
 * @returns {{ completed: number, total: number, percentage: number }} Progress object containing:
 *   - completed: Number of completed items in the section
 *   - total: Total number of items that can be completed
 *   - percentage: Percentage of completion (0-100)
 */
const calculateProgress = (params: ProgressCalculationParams) => {
  const progressMap = {
    event: eventProgress,
    organizers: userProgress,
    navigators: userProgress,
    documents: documentsProgress,
    judges: judgesProgress,
    teams: teamsProgress,
    artifacts: artifactsProgress,
    phases: phasesProgress,
  };

  return progressMap[params.eventKey as keyof typeof progressMap]?.(params);
};

/**
 * Custom hook to manage the state of event editing steps wizard
 * @param {Event} event - The event object being edited
 * @returns {Object} An object containing:
 *   - activeKey: Current active step key
 *   - setActiveKey: Function to change the active step
 *   - steps: Array of configured wizard steps with progress information
 *   - progress: Overall progress percentage across all steps
 *
 * @example
 * const { activeKey, setActiveKey, steps, progress } = useEditEventStepsState(event);
 */
export default function useEditEventStepsState({
  event,
  phases,
  contents,
  navigators,
  organizers,
  documents,
}: {
  event: Event;
  phases: Phase[];
  contents: Record<string, string>;
  navigators: EventUser[];
  organizers: EventUser[];
  documents: Document[];
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(EditEventSteps[0].eventKey);
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.hash) return;
    setActiveKey(location.hash.split('#').pop() || EditEventSteps[0].eventKey);
  }, [location.hash]);

  const onSetActiveKey = (key: string | number) => {
    navigate(`#${key}`, { replace: true });
    setActiveKey(String(key));
  };

  const { steps, progress } = useMemo(() => {
    if (!event) return { steps: EditEventSteps, progress: 0 };

    let progress = 0;
    const steps = EditEventSteps.map((step) => {
      const { completed, total, percentage } = calculateProgress({
        event,
        phases,
        contents,
        navigators,
        organizers,
        documents,
        eventKey: step.eventKey,
      });

      let variant: WizardNavItemProps['variant'] = percentage > 0 ? 'danger' : 'default';
      if (percentage === 100) variant = 'success';
      progress += percentage;
      return {
        ...step,
        label: t(step.label),
        subLabel: t(step.subLabel, { completed, total }),
        variant,
      };
    });
    return { steps, progress: Math.round(progress / steps.length) };
  }, [t, event, phases]);

  const nextStep = () => {
    const currentIndex = steps.findIndex((step) => step.eventKey === activeKey);
    const nextStep = steps[currentIndex + 1];
    if (nextStep) {
      setActiveKey(nextStep.eventKey);
    }
  };

  return { activeKey, setActiveKey: onSetActiveKey, steps, progress, nextStep };
}
